/**
 * Transient H-connector shift animation (home ↔ shifted).
 * Kept out of Redux so RAF ticks don't flood the store.
 *
 * Two phases:
 *  - closing: solid bar moves onto the OHT's approach leg (open → closed).
 *             OHT waits in place; does not ride.
 *  - transferring: solid bar + OHT slide to the opposite track.
 */
import type { ConnectorShiftState } from '../utils/connectorRail';

export type ConnectorShiftPhase = 'closing' | 'transferring';

export type ConnectorShiftAnim = {
  phase: ConnectorShiftPhase;
  from: ConnectorShiftState;
  to: ConnectorShiftState;
  /** Leg the OHT approached on (closing target / transfer start). */
  approachSide: ConnectorShiftState;
  /** 0 = still on `from` leg, 1 = arrived at `to` leg. */
  progress: number;
  /** Wall-clock ms when this phase started. */
  startedAt: number;
  /** OHT waiting or riding (always set for sim-driven transfers). */
  ohtId?: string;
  /** Along-track heading (deg) before transfer — restored after landing. */
  alongHeadingDeg?: number;
};

type Listener = () => void;

class ConnectorShiftStore {
  private anims = new Map<string, ConnectorShiftAnim>();
  /** Per-connector cooldown end time (ms). Survives after anim is cleared. */
  private cooldowns = new Map<string, number>();
  /** Per-OHT+connector cooldown so the same vehicle cannot bounce on the same H. */
  private ohtConnectorCooldowns = new Map<string, number>();
  /**
   * OHTs that just finished a transfer, keyed by ohtId → connector id.
   * That connector stays locked until they drive away; other H's remain usable.
   */
  private parked = new Map<string, string>();
  private listeners = new Set<Listener>();

  private ohtConnectorKey(ohtId: string, connectorId: string): string {
    return `${ohtId}::${connectorId}`;
  }

  getAnim(id: string): ConnectorShiftAnim | undefined {
    return this.anims.get(id);
  }

  getAll(): ReadonlyMap<string, ConnectorShiftAnim> {
    return this.anims;
  }

  /** Solid-leg lateral progress 0 (home) → 1 (far). Settled uses shiftState. */
  getSolidProgress(id: string, settled: ConnectorShiftState): number {
    const anim = this.anims.get(id);
    if (!anim) return settled === 'shifted' ? 1 : 0;
    if (anim.from === 'home' && anim.to === 'shifted') return anim.progress;
    if (anim.from === 'shifted' && anim.to === 'home') return 1 - anim.progress;
    return settled === 'shifted' ? 1 : 0;
  }

  isAnimating(id: string): boolean {
    const a = this.anims.get(id);
    return Boolean(a && a.progress < 1);
  }

  isCoolingDown(id: string, now = performance.now()): boolean {
    const until = this.cooldowns.get(id);
    return until != null && until > now;
  }

  /** True when this OHT is cooling down on a specific connector (re-entry lock). */
  isOhtCoolingDownOnConnector(
    ohtId: string,
    connectorId: string,
    now = performance.now()
  ): boolean {
    const until = this.ohtConnectorCooldowns.get(this.ohtConnectorKey(ohtId, connectorId));
    return until != null && until > now;
  }

  /**
   * @deprecated Prefer {@link isOhtCoolingDownOnConnector}. Kept for older call sites;
   * returns true if the OHT has any per-connector cooldown active.
   */
  isOhtCoolingDown(ohtId: string, now = performance.now()): boolean {
    const prefix = `${ohtId}::`;
    for (const [key, until] of this.ohtConnectorCooldowns) {
      if (key.startsWith(prefix) && until > now) return true;
    }
    return false;
  }

  getParkedConnector(ohtId: string): string | undefined {
    return this.parked.get(ohtId);
  }

  releaseParked(ohtId: string) {
    if (this.parked.delete(ohtId)) this.emit();
  }

  /** OHT ids blocked on a connector (waiting for close or riding across). */
  getBusyOhtIds(): Set<string> {
    const ids = new Set<string>();
    for (const anim of this.anims.values()) {
      if (anim.progress < 1 && anim.ohtId) ids.add(anim.ohtId);
    }
    return ids;
  }

  /** @deprecated use getBusyOhtIds — kept for call sites that meant "busy". */
  getRidingOhtIds(): Set<string> {
    return this.getBusyOhtIds();
  }

  /** OHT waiting while the solid bar closes onto its track. */
  isOhtWaiting(ohtId: string): boolean {
    for (const anim of this.anims.values()) {
      if (anim.progress < 1 && anim.phase === 'closing' && anim.ohtId === ohtId) {
        return true;
      }
    }
    return false;
  }

  /** OHT riding the solid bar during the cross-track transfer. */
  isOhtRiding(ohtId: string): boolean {
    for (const anim of this.anims.values()) {
      if (anim.progress < 1 && anim.phase === 'transferring' && anim.ohtId === ohtId) {
        return true;
      }
    }
    return false;
  }

  start(params: {
    id: string;
    phase: ConnectorShiftPhase;
    from: ConnectorShiftState;
    to: ConnectorShiftState;
    approachSide: ConnectorShiftState;
    now?: number;
    ohtId?: string;
    alongHeadingDeg?: number;
    /** Skip cooldown checks when chaining closing → transferring. */
    force?: boolean;
  }) {
    const {
      id,
      phase,
      from,
      to,
      approachSide,
      now = performance.now(),
      ohtId,
      alongHeadingDeg,
      force = false,
    } = params;

    if (from === to) return;
    if (!force && this.isAnimating(id)) return;
    if (!force && this.isCoolingDown(id, now)) return;
    if (
      !force &&
      ohtId &&
      this.isOhtCoolingDownOnConnector(ohtId, id, now)
    ) {
      return;
    }

    this.anims.set(id, {
      phase,
      from,
      to,
      approachSide,
      progress: 0,
      startedAt: now,
      ohtId,
      alongHeadingDeg,
    });
    this.emit();
  }

  /**
   * Advance all animations. `durationMs` may be a number or a per-anim resolver
   * (closing vs transferring use different lengths).
   */
  tick(
    now: number,
    durationMs: number | ((anim: ConnectorShiftAnim) => number)
  ): string[] {
    const completed: string[] = [];
    let changed = false;
    for (const [id, anim] of this.anims) {
      if (anim.progress >= 1) continue;
      const dur =
        typeof durationMs === 'function' ? durationMs(anim) : durationMs;
      const next = Math.min(1, Math.max(0, (now - anim.startedAt) / Math.max(1, dur)));
      if (now < anim.startedAt) {
        // Seek/restart rewound the sim clock behind this anim — restart cleanly.
        anim.startedAt = now;
        anim.progress = 0;
        changed = true;
        continue;
      }
      if (next !== anim.progress) {
        anim.progress = next;
        changed = true;
      }
      if (anim.progress >= 1) {
        completed.push(id);
      }
    }
    if (changed) this.emit();
    return completed;
  }

  clear(id: string) {
    if (!this.anims.has(id)) return;
    this.anims.delete(id);
    this.emit();
  }

  /**
   * Mark transfer finished: cooldown + park latch so the OHT does not
   * immediately bounce back on the *same* connector. Other H-connectors
   * on the destination rail remain usable (dual-connector loops).
   */
  markSettled(id: string, now = performance.now(), cooldownMs = 1200) {
    const prev = this.anims.get(id);
    if (!prev) return;

    this.cooldowns.set(id, now + cooldownMs);
    if (prev.ohtId) {
      this.ohtConnectorCooldowns.set(
        this.ohtConnectorKey(prev.ohtId, id),
        now + cooldownMs
      );
      this.parked.set(prev.ohtId, id);
    }
    this.anims.delete(id);
    this.emit();
  }

  /** Test helper — wipe all transient transfer state. */
  reset() {
    this.anims.clear();
    this.cooldowns.clear();
    this.ohtConnectorCooldowns.clear();
    this.parked.clear();
    this.emit();
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }
}

export const connectorShiftStore = new ConnectorShiftStore();
